import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import logo from "@/assets/amigo-logo.png";
import { UtilityBar } from "./UtilityBar";
import { MobileNavigation } from "./MobileNavigation";
import { BOOKING_URL, NAV_LINKS, routeTo, TOUR_360_URL } from "@/lib/site";

/** Sticky header: utility bar + brand + navigation + booking CTA. */
export function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50">
      <div className={cn("transition-all duration-300", scrolled && "hidden lg:block")}>
        <UtilityBar />
      </div>

      <div
        className={cn(
          "bg-surface/95 backdrop-blur-md transition-all duration-300",
          scrolled
            ? "border-b border-sand shadow-[var(--shadow-soft)]"
            : "border-b border-transparent",
        )}
      >
        <div
          className={cn(
            "mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-4 transition-all duration-300 sm:px-6 lg:px-10",
            scrolled ? "h-16" : "h-20 lg:h-[5.5rem]",
          )}
        >
          <Link to="/" className="flex shrink-0 items-center" aria-label="Amigo Inn & RV Park home">
            <img
              src={logo}
              alt="Amigo Inn & RV Park"
              width={300}
              height={100}
              className={cn(
                "w-auto transition-all duration-300",
                scrolled ? "h-9" : "h-11 lg:h-12",
              )}
            />
          </Link>

          <nav aria-label="Main" className="hidden lg:block">
            <ul className="flex items-center gap-8">
              {NAV_LINKS.map((item) => (
                <li key={item.href}>
                  <Link
                    to={routeTo(item.href)}
                    activeOptions={{ exact: item.href === "/" }}
                    activeProps={{ className: "text-forest after:scale-x-100" }}
                    className="link-underline py-2 text-sm font-semibold text-ink/85 transition-colors hover:text-forest"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="flex items-center gap-3">
            {/* TODO: verify 360° tour URL before production deployment */}
            <a
              href={TOUR_360_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="link-underline hidden text-sm font-semibold text-forest xl:inline-flex"
            >
              360° Tour <ArrowUpRight className="size-4" aria-hidden="true" />
            </a>
            <Button asChild variant="book" size="cta" className="hidden sm:inline-flex">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Book Your Stay
              </a>
            </Button>
            <MobileNavigation />
          </div>
        </div>
      </div>
    </header>
  );
}
